/**
 * In-lesson Quiz tab - formal active recall.
 *
 * Loads the stored `LessonQuiz.questions` (generated by `quiz_task` and
 * tagged with `concept_id`) and walks the user through them. After all
 * questions are answered, per-concept correctness aggregates into a single
 * FSRS grade per concept and is submitted via the shared review endpoint:
 *
 *   100% → grade 4 (Easy)   ≥50% → 3 (Good)   >0% → 2 (Hard)   0 → 1 (Again)
 *
 * State is persisted (localStorage, keyed by lesson) so the run survives
 * sub-tab switches and full reloads - a finished quiz shows its results on
 * return; an in-progress one resumes. FSRS reviews use a per-lesson daily
 * idempotency key, so a same-day retry never re-grades (see practiceAttemptKey).
 * Concepts whose `mem0_memory_id` hasn't resolved yet (mindspace sync still in
 * flight) are shown in the summary but not submitted - they'd 404 the endpoint.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { track } from '@/config/observability';
import { notify } from '@/lib/notify';
import { Button } from '@ai-tutor/ui';
import { BlockMarkdown } from '@/components/core/markdown/markdown';
import { QuizSkeleton } from '../skeletons/PanelSkeletons';
import type {
  ConceptInLesson,
  QuizQuestion as ApiQuizQuestion,
  QuizQuestionAttemptPayload,
} from '@/lib/api-client';
import { quizAttemptApi } from '@/lib/api-client';
import { useSubmitReview } from '@/hooks/mindspace/useRevision';
import {
  useQuizSession,
  type PersistedQuizSession,
} from '@/hooks/practice/useQuizSession';
import { writtenSignature } from '@/lib/practice/signature';
import { practiceFsrsAttemptId } from '@/lib/practice/attemptKey';
import { buildConceptResolver } from '@/lib/practice/concepts';
import {
  ratioToGrade,
  type ConceptGrade,
  type ReviewOutcomes,
  submitConceptGrades,
} from '@/lib/practice/grades';
import type { Question } from '@/data/fixtures';
import { QuestionRenderer } from './QuestionRenderer';
import { QuizRunnerHeader } from './QuizRunnerHeader';
import { QuizCompletedView } from './QuizCompletedView';
import { QuizActionBar } from './QuizActionBar';
import { PRACTICE_CARD } from './practiceSurface';
import { cn } from '@/lib/utils';

interface LessonQuizRunnerProps {
  /** Stored quiz questions for this lesson - concept_id required per question. */
  questions: ApiQuizQuestion[];
  /** Lesson concepts (used for the cross-walk to mem0_memory_id). */
  concepts: ConceptInLesson[];
  /** Lesson identifier - required to submit LessonQuizAttempt, whose
   *  per-question results feed concept retention / mastery on the
   *  Progress page. */
  lessonId?: string;
  isLoading?: boolean;
  /** Optional hook for the parent to offer "regenerate quiz" UI on completion. */
  onCompleted?: () => void;
  /**
   * Rebuild the quiz — offered when it came back with no questions. That
   * state used to say "Generate the quiz from the tab header", a control that
   * does not exist (`Layout/Lesson/Viewer/Quiz/NoQuestions`).
   */
  onRegenerate?: () => void;
  /** True while a regenerate request is in flight. */
  regenerating?: boolean;
  /** Go to the flashcards - offered on the end screen. */
  onPractiseFlashcards?: () => void;
}

interface QuestionWithIndex {
  index: number;
  question: ApiQuizQuestion;
  rendered: Question;
}

export function LessonQuizRunner({
  questions,
  concepts,
  lessonId,
  isLoading,
  onCompleted,
  onRegenerate,
  regenerating = false,
  onPractiseFlashcards,
}: LessonQuizRunnerProps) {
  const submit = useSubmitReview();

  const items = useMemo<QuestionWithIndex[]>(
    () =>
      questions.map((q, i) => ({
        index: i,
        question: q,
        rendered: mapApiQuestionToRendered(q, i),
      })),
    [questions]
  );

  // Resolves a question's concept_id (per-lesson OR canonical) to its concept +
  // the canonical id FSRS submits against.
  const resolveConcept = useMemo(
    () => buildConceptResolver(concepts),
    [concepts]
  );

  // Persisted session - survives sub-tab remount and page reload; resets when
  // the quiz is regenerated (content signature changes). Questions made from
  // a concept's picture are left out: they are appended after the written
  // ones when the picture is ready, maybe mid-session, and must not reset it.
  const signature = useMemo(
    () => writtenSignature(questions, q => q.question),
    [questions]
  );
  const { session, update, reset } = useQuizSession(
    lessonId ?? 'unknown',
    signature,
    questions.length > 0
  );

  // Transient in-flight flag (never persisted).
  const [isSubmitting, setIsSubmitting] = useState(false);

  const total = items.length;
  const activeIndex = Math.min(session.activeIndex, Math.max(0, total - 1));
  const current = items[activeIndex];

  // ---- Submission (single code path: completing the quiz sets `completed`,
  // the effect below submits). Reused on reload if a completed session never
  // reached `submission:'done'` (e.g. tab closed mid-submit) - idempotent. ----
  const submitResults = useCallback(
    async (sess: PersistedQuizSession) => {
      setIsSubmitting(true);
      // Per-lesson daily FSRS key: shared with flashcards (first-wins) and
      // stable across same-day retries (no re-grade).
      const fsrsAttemptId = practiceFsrsAttemptId(lessonId ?? 'unknown');

      // LessonQuizAttempt row (Progress trend) uses a per-attempt uuid so every
      // attempt is tracked, even when the FSRS grade is deduped.
      if (lessonId) {
        const perQuestion: QuizQuestionAttemptPayload[] = items.map(it => ({
          question_id: String(it.index),
          concept_id: it.question.concept_id ?? null,
          correct: !!sess.verdicts[it.index],
        }));
        const score = Object.values(sess.verdicts).filter(Boolean).length;
        track('quiz_submitted', {
          lesson_id: lessonId,
          score,
          max_score: items.length,
        });
        try {
          await quizAttemptApi.submit(lessonId, {
            attempt_id: sess.attemptRowId,
            score,
            max_score: items.length,
            per_question: perQuestion,
          });
        } catch {
          // Non-fatal - the trend misses this attempt; reviews still go through.
        }
      }

      const grades = aggregatePerConceptGrades(items, sess.verdicts);
      const { outcomes, failed } = await submitConceptGrades(grades, {
        canonicalId: id => resolveConcept(id)?.canonicalId ?? undefined,
        submit: vars => submit.mutateAsync(vars),
        attemptId: fsrsAttemptId,
        lessonId,
      });
      if (failed > 0) {
        notify.error(
          `Failed to record ${failed} review${failed === 1 ? '' : 's'}`
        );
      }
      update({ submission: 'done', reviewOutcomes: outcomes });
      setIsSubmitting(false);
      onCompleted?.();
    },
    [items, resolveConcept, lessonId, submit, update, onCompleted]
  );

  // Fire submission once per mount when a completed run hasn't been submitted
  // yet. Guarded on content + concepts loaded so we never mark 'done' before
  // the crosswalk exists. `firedRef` prevents duplicate fires within a mount;
  // the daily key makes even a stray duplicate a server no-op.
  const firedRef = useRef(false);
  useEffect(() => {
    if (
      session.completed &&
      session.submission === 'idle' &&
      !firedRef.current &&
      items.length > 0 &&
      concepts.length > 0
    ) {
      firedRef.current = true;
      void submitResults(session);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.completed, session.submission, items.length, concepts.length]);

  const handleSubmitAnswer = () => {
    if (session.selectedAnswer === null || !current) return;
    const correct = checkAnswer(current.rendered, session.selectedAnswer);
    update({
      verdicts: { ...session.verdicts, [activeIndex]: correct },
      answers: { ...session.answers, [activeIndex]: session.selectedAnswer },
      showExplanation: true,
    });
  };

  const handleNext = () => {
    if (activeIndex < total - 1) {
      update({
        activeIndex: activeIndex + 1,
        selectedAnswer: null,
        showExplanation: false,
      });
      return;
    }
    update({ completed: true }); // effect submits
  };

  const handleRestart = () => {
    setIsSubmitting(false);
    firedRef.current = false;
    reset(); // new attemptRowId; same daily FSRS key → same-day retry won't re-grade
  };

  if (isLoading) {
    return <QuizSkeleton mode='fetching' />;
  }

  if (total === 0) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-2 p-8 text-center'>
        <p className='text-sm font-medium text-foreground'>
          No quiz questions for this lesson yet.
        </p>
        <p className='text-xs text-muted-foreground'>
          The quiz came back empty. Regenerating it usually fixes this.
        </p>
        {onRegenerate && (
          <Button
            size='sm'
            className='mt-2'
            onClick={onRegenerate}
            disabled={regenerating}
          >
            {regenerating ? 'Regenerating…' : 'Regenerate quiz'}
          </Button>
        )}
      </div>
    );
  }

  if (session.completed) {
    return (
      <QuizCompletedView
        total={total}
        correct={Object.values(session.verdicts).filter(Boolean).length}
        conceptGrades={aggregatePerConceptGrades(items, session.verdicts)}
        resolveConcept={resolveConcept}
        outcomes={session.reviewOutcomes}
        submission={isSubmitting ? 'submitting' : session.submission}
        onRestart={handleRestart}
        mistakes={items
          .filter(it => session.verdicts[it.index] === false)
          .map(it => ({
            question: it.rendered,
            given: session.answers?.[it.index],
          }))}
        onPractiseFlashcards={onPractiseFlashcards}
      />
    );
  }

  if (!current) return null;

  // The question scrolls; the action bar stays pinned under it.
  return (
    <div className='mx-auto flex h-full w-full max-w-2xl flex-col'>
      <div className='min-h-0 flex-1 overflow-y-auto p-4 sm:p-6'>
        <QuizRunnerHeader
          index={activeIndex}
          total={total}
          score={Object.values(session.verdicts).filter(Boolean).length}
          scoredCount={Object.keys(session.verdicts).length}
          onRestart={handleRestart}
        />
        <div className={cn(PRACTICE_CARD, 'p-4 sm:p-6')}>
          <BlockMarkdown className='mb-4 text-lg font-medium text-foreground sm:mb-5 sm:text-xl'>
            {current.rendered.question}
          </BlockMarkdown>
          <QuestionRenderer
            question={current.rendered}
            selectedAnswer={session.selectedAnswer}
            showExplanation={session.showExplanation}
            onAnswerSelect={answer => update({ selectedAnswer: answer })}
          />
        </div>
      </div>
      <QuizActionBar
        question={current.rendered}
        revealed={session.showExplanation}
        correct={!!session.verdicts[activeIndex]}
        canSubmit={session.selectedAnswer !== null}
        isLast={activeIndex === total - 1}
        onSubmit={handleSubmitAnswer}
        onNext={handleNext}
      />
    </div>
  );
}

// ===========================================================================
// MAPPING + AGGREGATION HELPERS
// ===========================================================================

/**
 * Map an API quiz question into the internal Question shape used by
 * `QuestionRenderer`. Kept inline so the runner is self-contained.
 */
function mapApiQuestionToRendered(q: ApiQuizQuestion, index: number): Question {
  const typeMap: Record<ApiQuizQuestion['type'], Question['type']> = {
    mcq: 'multiple-choice',
    true_false: 'true-false',
    fill_blank: 'fill-blank',
    multi_select: 'multiple-select',
  };

  let correctAnswer: string | number | number[] = q.correct_answer as string;

  if (q.type === 'mcq' && q.options.length > 0) {
    const idx = q.options.indexOf(q.correct_answer as string);
    correctAnswer = idx >= 0 ? idx : (q.correct_answer as string);
  } else if (q.type === 'multi_select' && Array.isArray(q.correct_answer)) {
    correctAnswer = q.correct_answer
      .map(a => q.options.indexOf(a))
      .filter(i => i >= 0);
  } else if (q.type === 'true_false') {
    correctAnswer = (q.correct_answer as string).toLowerCase();
  }

  return {
    id: `q-${index}`,
    type: typeMap[q.type],
    question: q.question,
    options: q.options,
    correctAnswer,
    explanation: q.explanation,
    visual: q.visual ?? undefined,
    explanationVisual: q.explanation_visual ?? undefined,
  };
}

function checkAnswer(
  question: Question,
  answer: number | string | number[]
): boolean {
  const { correctAnswer } = question;
  if (Array.isArray(correctAnswer)) {
    if (!Array.isArray(answer)) return false;
    return (
      answer.length === correctAnswer.length &&
      answer.every(a => correctAnswer.includes(a))
    );
  }
  if (question.type === 'fill-blank') {
    const accepted = question.acceptedAnswers || [correctAnswer as string];
    const userAnswer = answer as string;
    return question.caseSensitive === false
      ? accepted.some(
          a => (a as string).toLowerCase() === userAnswer.toLowerCase()
        )
      : accepted.includes(userAnswer);
  }
  return answer === correctAnswer;
}

/**
 * Group per-question verdicts by concept_id and project a single FSRS grade
 * per concept. Questions without a concept_id are skipped.
 */
function aggregatePerConceptGrades(
  items: QuestionWithIndex[],
  verdicts: Record<number, boolean>
): ConceptGrade[] {
  const buckets = new Map<string, { correct: number; total: number }>();
  for (const item of items) {
    const cid = item.question.concept_id;
    if (!cid) continue;
    const verdict = verdicts[item.index];
    if (verdict === undefined) continue;
    const slot = buckets.get(cid) ?? { correct: 0, total: 0 };
    slot.total += 1;
    if (verdict) slot.correct += 1;
    buckets.set(cid, slot);
  }

  const grades: ConceptGrade[] = [];
  for (const [conceptId, { correct, total }] of buckets.entries()) {
    if (total === 0) continue;
    grades.push({ conceptId, grade: ratioToGrade(correct / total) });
  }
  return grades;
}
